<script>
import { Head } from '@inertiajs/inertia-vue3';
import { h } from 'vue';

/* The document head on the client side.
 *
 * Every page renders this component and takes no props. The head is not declared
 * per page any more: `app/Support/Seo.php` resolves it on the server, the same
 * value is rendered into the first response by `app.blade.php`, and the same
 * value arrives here as the shared `seo` prop. One resolution, two renderers, so
 * the tags a crawler reads out of the HTML and the tags a client-side navigation
 * writes cannot disagree.
 *
 * The per-page props this used to take are gone on purpose. Thirteen pages each
 * declaring their own title was thirteen chances for the separator, the
 * publisher or the description to drift, and it was worse than that: none of the
 * thirteen existed in the first response, so the head a shared link produced was
 * whatever the Blade template said. A page that wants a different head now says
 * so where its URL is decided, through `Seo::set()`, because that is the only
 * layer a crawler is guaranteed to reach.
 *
 * This is a render function rather than a template on purpose, and it must stay
 * one.
 *
 * `@inertiajs/inertia-vue3` 0.6 walks the slot it is given and turns each vnode
 * into a tag by stringifying `node.type`. It knows Vue's internal vnodes by the
 * old *descriptions*: it tests for `'Symbol(Comment)'`, `'Symbol(Text)'` and
 * `'Symbol(Fragment)'`. Vue 3.3 changed those to `Fragment = Symbol.for('v-fgt')`,
 * `Text = Symbol.for('v-txt')`, `Comment = Symbol.for('v-cmt')`, so not one of
 * those branches matches any more, and a comment vnode falls through to the tag
 * builder:
 *
 *   return `<${node.type}${attrs}>`;   // node.type is a Symbol -> TypeError
 *
 * `Cannot convert a Symbol value to a string`, thrown inside this render, which
 * leaves the page broken. A `v-if` in a template compiles to
 * `createCommentVNode('v-if', true)`, so the moment this component had
 * `<meta v-if="robots">` in it, every page that did not pass `robots` died on
 * load.
 *
 * Building the child list here is what makes that impossible: the array can only
 * ever contain element vnodes, and a Fragment and a Comment are the two things a
 * template would have added. Do not move this back into a template, and never
 * put a `v-if`, `v-for` or bare text node inside `<Head>`.
 */
/* Inertia's head manager builds each tag by concatenating the value straight
 * into `name="value"` with no escaping (`renderTagStart` in
 * `@inertiajs/inertia-vue3`), so a title or description carrying a quote or an
 * ampersand closes the attribute early and hands the parser a malformed tag.
 * The server renderer escapes through Blade's `{{ }}`, so without this the same
 * head would be well formed in the first response and broken after a
 * client-side visit. The five characters are exactly the ones htmlspecialchars
 * converts, and `&` goes first so the entities it introduces are not escaped a
 * second time. */
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export default {
  computed: {
    /* The head the server resolved for this request. Optional chaining through
       every level, because this component renders on the very first paint as
       well, and a head-less response must degrade to no tags rather than throw. */
    seo() {
      return (this.$page && this.$page.props && this.$page.props.seo) || {};
    },

    /* The structured data as the one-paragraph JSON-LD document the crawlers
       read, with the same graph wrapper the Blade template writes.

       `<` and `>` are escaped to their JSON unicode escapes. Inertia builds the
       script element by parsing a template string, so an unescaped closing script
       tag arriving in a blog title would close the element early and hand the
       parser a truncated document. The escapes are inside JSON string literals, so
       the data the crawler reads back is unchanged. The Blade side does the same
       thing with JSON_HEX_TAG. */
    jsonLd() {
      if (!Array.isArray(this.seo.jsonLd) || this.seo.jsonLd.length === 0) {
        return null;
      }

      const graph = {
        '@context': 'https://schema.org',
        '@graph': this.seo.jsonLd
      };

      return JSON.stringify(graph).replace(/</g, '\\u003C').replace(/>/g, '\\u003E');
    }
  },

  render() {
    const seo = this.seo;
    const tags = [];

    /* The title is always present: the resolver falls back to the site name
       rather than to nothing, so no page can end up without one. */
    if (seo.title) {
      tags.push(h('title', { 'head-key': 'title' }, escapeHtml(seo.title)));
    }

    if (seo.description) {
      tags.push(h('meta', { 'head-key': 'description', name: 'description', content: escapeHtml(seo.description) }));
    }

    /* Always rendered, including on the pages that are allowed to be indexed.
       An explicit `index, follow` is not noise: it is what overrides a `Disallow`
       or a `noindex` inherited from a parent path, and it is the tag a crawler
       reads to know the page was considered rather than forgotten. */
    if (seo.robots) {
      tags.push(h('meta', { 'head-key': 'robots', name: 'robots', content: escapeHtml(seo.robots) }));
    }

    /* Absent on every page that must not be indexed. See Seo::build(). */
    if (seo.canonical) {
      tags.push(h('link', { 'head-key': 'canonical', rel: 'canonical', href: escapeHtml(seo.canonical) }));
    }

    /* One link per language, plus x-default. The head-key includes the hreflang
       so Inertia keeps all of them instead of replacing the first with the next. */
    if (Array.isArray(seo.alternates)) {
      seo.alternates.forEach((alternate) => {
        if (!alternate || !alternate.hreflang || !alternate.href) {
          return;
        }

        tags.push(h('link', {
          'head-key': 'alternate-' + alternate.hreflang,
          rel: 'alternate',
          hreflang: escapeHtml(alternate.hreflang),
          href: escapeHtml(alternate.href)
        }));
      });
    }

    /* The social card, for public pages only. A page that carries noindex is
       somebody's screen rather than a page to share, so it advertises nothing.
       The server renderer in `app.blade.php` draws the same line. */
    if (seo.indexable) {
      tags.push(h('meta', { 'head-key': 'og:type', property: 'og:type', content: escapeHtml(seo.type) }));
      tags.push(h('meta', { 'head-key': 'og:site_name', property: 'og:site_name', content: escapeHtml(seo.siteName) }));
      tags.push(h('meta', { 'head-key': 'og:locale', property: 'og:locale', content: escapeHtml(seo.locale) }));

      if (Array.isArray(seo.localeAlternates)) {
        seo.localeAlternates.forEach((locale) => {
          tags.push(h('meta', {
            'head-key': 'og:locale:alternate-' + locale,
            property: 'og:locale:alternate',
            content: escapeHtml(locale)
          }));
        });
      }
      tags.push(h('meta', { 'head-key': 'og:title', property: 'og:title', content: escapeHtml(seo.title) }));

      if (seo.description) {
        tags.push(h('meta', { 'head-key': 'og:description', property: 'og:description', content: escapeHtml(seo.description) }));
      }

      if (seo.canonical) {
        tags.push(h('meta', { 'head-key': 'og:url', property: 'og:url', content: escapeHtml(seo.canonical) }));
      }

      if (seo.image) {
        tags.push(h('meta', { 'head-key': 'og:image', property: 'og:image', content: escapeHtml(seo.image) }));
        tags.push(h('meta', { 'head-key': 'og:image:alt', property: 'og:image:alt', content: escapeHtml(seo.imageAlt) }));

        /* Only declared when the real dimensions are known, because they have to
           match the file. A wrong pair is worse than none: the crawler would
           reserve the wrong box and crop the card. */
        if (seo.imageWidth && seo.imageHeight) {
          tags.push(h('meta', { 'head-key': 'og:image:width', property: 'og:image:width', content: String(seo.imageWidth) }));
          tags.push(h('meta', { 'head-key': 'og:image:height', property: 'og:image:height', content: String(seo.imageHeight) }));
        }
      }

      if (seo.publishedTime) {
        tags.push(h('meta', { 'head-key': 'article:published_time', property: 'article:published_time', content: escapeHtml(seo.publishedTime) }));
      }

      if (seo.modifiedTime) {
        tags.push(h('meta', { 'head-key': 'article:modified_time', property: 'article:modified_time', content: escapeHtml(seo.modifiedTime) }));
      }

      tags.push(h('meta', { 'head-key': 'twitter:card', name: 'twitter:card', content: 'summary_large_image' }));
      tags.push(h('meta', { 'head-key': 'twitter:title', name: 'twitter:title', content: escapeHtml(seo.title) }));

      if (seo.description) {
        tags.push(h('meta', { 'head-key': 'twitter:description', name: 'twitter:description', content: escapeHtml(seo.description) }));
      }

      if (seo.image) {
        tags.push(h('meta', { 'head-key': 'twitter:image', name: 'twitter:image', content: escapeHtml(seo.image) }));
      }
    }

    /* The JSON-LD is a script body rather than an attribute, so it keeps its own
       escaping above and is never run through escapeHtml: `&` and `"` are literal
       inside a script element, and entity-encoding them would change the document
       the crawler reads back. */
    if (this.jsonLd) {
      tags.push(h('script', { 'head-key': 'json-ld', type: 'application/ld+json' }, this.jsonLd));
    }

    return h(Head, null, { default: () => tags });
  }
};
</script>
